import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { Card, Row, Col, Button, Spinner } from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Form,
  Input,
  InputNumber,
  message,
  Table,
  Checkbox,
  Button as AntButton,
  Select,
  Switch,
  Card as AntCard,
  Alert as AntAlert,
} from "antd";
import {
  PlusCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import { indianCurrencyFormatter, indianCurrencyParser } from "../../utils/Utils";
import workOrderApis from "../../api/WorkOrderManagement/WorkOrderApis";
import sequenceApis from "../../api/master/sequence";

const WorkOrderAdvance: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();

  const [workOrder, setWorkOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [updatingRecovery, setUpdatingRecovery] = useState(false);
  const [sequenceAccess, setSequenceAccess] = useState<any>();
  const [advanceItems, setAdvanceItems] = useState<any[]>([]);
  const [initialItemIds, setInitialItemIds] = useState<Set<number>>(new Set());
  const [initialItems, setInitialItems] = useState<any[]>([]); // Store initial state for comparison
  const validationTimeoutRef = useRef<{ [key: number]: NodeJS.Timeout }>({});
  const [showRecoveryConditions, setShowRecoveryConditions] = useState(false);
  const [mobilizationAdvanceAmount, setMobilizationAdvanceAmount] = useState<number>(0);
  const [recoveryConditions, setRecoveryConditions] = useState<{
    recoveryType: string;
    recoveryPercentRate: number;
    recoveryCapPercent: number;
    interestCharged: boolean;
    interestRate: number;
  }>({
    recoveryType: "Each RA Bill",
    recoveryPercentRate: 0,
    recoveryCapPercent: 0,
    interestCharged: false,
    interestRate: 0,
  });

  useEffect(() => {
    fetchSequenceAccess();
    if (id) {
      loadWorkOrderData(id);
    }
  }, [id]);

  const loadWorkOrderData = async (workOrderId: string) => {
    try {
      setLoading(true);
      const response: any = await workOrderApis.getWorkOrderById(workOrderId);

      if (response && response.status && response.data) {
        const apiData = response.data;
        setWorkOrder(apiData);

        // Load existing Advance items from workOrderAdvanceDeductions (both Advance and Mobilization Advance)
        const existingItems =
          apiData.workOrderAdvanceDeductions?.filter(
            (item: any) =>
              item.type === "Advance" || item.type === "Mobilization Advance"
          ) || [];

        if (existingItems.length > 0) {
          // Map API response to form format
          const mappedItems = existingItems.map((item: any, index: number) => ({
            id: item.workOrderAdvanceDeductionId || index + 1,
            workOrderAdvanceDeductionId: item.workOrderAdvanceDeductionId,
            name: item.name || "",
            type: item.type || "Advance", // Include type from API
            isChargeable: true, // Always checked
            amount: item.totalAmount || 0, // Show totalAmount on initial load
            remarks: item.notes || "",
            totalPendingRecoveredAmount: item.totalPendingRecoveredAmount || 0,
            balanceAmount: item.balanceAmount || 0,
            totalRecoveredAmount: item.totalRecoveredAmount || 0,
            isExisting: true, // Flag to identify existing items
          }));
          setAdvanceItems(mappedItems);
          // Store initial items for comparison
          setInitialItems(JSON.parse(JSON.stringify(mappedItems)));
          // Track initial item IDs to identify deleted items
          const initialIds = new Set<number>(
            existingItems
              .map((item: any) => item.workOrderAdvanceDeductionId)
              .filter((id: any) => id != null)
              .map((id: any) => Number(id))
          );
          setInitialItemIds(initialIds);
        } else {
          // Initialize with one empty row if no existing data
          const emptyItem = {
            id: 1,
            name: "",
            type: "Advance", // Default type
            isChargeable: true, // Always checked
            amount: 0,
            remarks: "",
            isExisting: false,
          };
          setAdvanceItems([emptyItem]);
          setInitialItems([]); // No initial items
        }
        // Check mobilization payment condition
        // Show recovery card when mobilization advance is NOT present, OR when it is
        // present but recovery % / recovery cap % is not captured (0, null, or undefined).
        const mobilizationCondition =
          apiData.workOrderPaymentConditions?.find(
            (c: any) => c.conditionType === "Mobilization"
          );
        const mobilizationAmount =
          Number(mobilizationCondition?.calculatedAmount) ||
          Number(mobilizationCondition?.amountValue) ||
          0;
        const hasMobilizationAdvance =
          !!mobilizationCondition && mobilizationAmount > 0;

        const recoveryPercentRate =
          Number(mobilizationCondition?.recoveryPercentRate) || 0;
        const recoveryCapPercent =
          Number(mobilizationCondition?.recoveryCapPercent) || 0;
        const recoveryNotCaptured =
          recoveryPercentRate <= 0 || recoveryCapPercent <= 0;

        setMobilizationAdvanceAmount(
          hasMobilizationAdvance ? mobilizationAmount : 0
        );

        if (!recoveryNotCaptured) {
          setShowRecoveryConditions(false);
        } else {
          setShowRecoveryConditions(true);
          // Pre-fill from existing condition if available
          if (mobilizationCondition) {
            setRecoveryConditions({
              recoveryType:
                mobilizationCondition.recoveryType || "Each RA Bill",
              recoveryPercentRate: recoveryPercentRate,
              recoveryCapPercent: recoveryCapPercent,
              interestCharged:
                mobilizationCondition.interestCharged || false,
              interestRate: mobilizationCondition.interestRate || 0,
            });
          }
        }
      } else {
        message.error("Failed to load work order data");
        navigate(-1);
      }
    } catch (error) {
      console.error("Error loading work order:", error);
      message.error("Failed to load work order data");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const fetchSequenceAccess = async () => {
    try {
      const response = await sequenceApis.fetchSequence();
      const { data } = response;
      const today = new Date();
      const _sequenceAccess = data?.filter(
        (item: any) =>
          item.moduleName === "Work Order Advance" &&
          new Date(item.endDate) > today &&
          item.status === "Active"
      );
      if (_sequenceAccess.length > 0) {
        setSequenceAccess({
          module: "Work Order Advance",
          level: _sequenceAccess[0].level,
          siteIds: _sequenceAccess?.map((item: any) =>
            item?.siteId?.toString()
          ),
        });
      } else {
        setSequenceAccess({
          module: "",
          level: "",
          siteIds: [],
        });
      }
    } catch (error: any) {
      console.log(error.response?.data?.message || "An error occurred");
    }
  };

  const validateForm = () => {
    // Only validate new items (existing items are read-only)
    const newItems = advanceItems.filter((item) => !item.isExisting);

    if (newItems.length === 0) {
      message.error("Please add at least one new advance item");
      return false;
    }

    // Validate that all new items have name
    const itemsWithEmptyName = newItems.filter(
      (item) => !item.name || item.name.trim() === ""
    );

    // Validate that all new items have amount greater than 0
    const itemsWithZeroAmount = newItems.filter(
      (item) => !item.amount || item.amount === 0 || item.amount === ""
    );

    // Validate remarks - mandatory for all new items
    const itemsWithEmptyRemarks = newItems.filter(
      (item) => !item.remarks || item.remarks.trim() === ""
    );

    if (itemsWithEmptyName.length > 0) {
      message.error("Please fill name for all items");
      return false;
    }

    if (itemsWithZeroAmount.length > 0) {
      message.error("Please fill amount for all items");
      return false;
    }

    if (itemsWithEmptyRemarks.length > 0) {
      message.error("Please fill remarks for all items");
      return false;
    }

    return true;
  };

  const handleAddRow = () => {
    const newId = Math.max(...advanceItems.map((item) => item.id), 0) + 1;
    setAdvanceItems([
      ...advanceItems,
      {
        id: newId,
        name: "",
        type: "Advance", // Default type
        isChargeable: true, // Always checked
        amount: 0,
        remarks: "",
        isExisting: false,
      },
    ]);
  };

  const handleDeleteRow = (id: number) => {
    const itemToDelete = advanceItems.find((item) => item.id === id);

    // Check if it's an existing item with recovered amount
    if (
      itemToDelete?.isExisting &&
      (itemToDelete?.totalRecoveredAmount || 0) > 0
    ) {
      message.error(
        "Cannot delete item with recovered amount. This item has already been recovered."
      );
      return;
    }

    if (advanceItems.length === 1) {
      message.warning("At least one row is required");
      return;
    }

    setAdvanceItems(advanceItems.filter((item) => item.id !== id));
  };

  const handleItemChange = (id: number, field: string, value: any) => {
    setAdvanceItems(
      advanceItems.map((item) => {
        if (item.id === id) {
          // Validate amount with debounce for existing items
          if (field === "amount" && item.isExisting) {
            // Clear existing timeout for this item
            if (validationTimeoutRef.current[id]) {
              clearTimeout(validationTimeoutRef.current[id]);
            }

            // Set new timeout to validate after user stops typing (500ms delay)
            validationTimeoutRef.current[id] = setTimeout(() => {
              setAdvanceItems((prevItems) => {
                const currentItem = prevItems.find((i) => i.id === id);
                if (currentItem?.isExisting) {
                  const totalRecovered = currentItem.totalRecoveredAmount || 0;
                  const totalPending =
                    currentItem.totalPendingRecoveredAmount || 0;
                  const cumulativeRecoveredAmount =
                    totalRecovered + totalPending;

                  // Handle all cases: null, undefined, empty string, or numeric value
                  let newAmount = 0;
                  const currentValue = currentItem.amount;
                  if (
                    currentValue === null ||
                    currentValue === undefined ||
                    currentValue === ""
                  ) {
                    newAmount = 0;
                  } else {
                    const parsed = Number(currentValue);
                    newAmount = isNaN(parsed) ? 0 : parsed;
                  }

                  // Check and show error if amount is less than cumulative recovered amount
                  if (
                    cumulativeRecoveredAmount > 0 &&
                    newAmount < cumulativeRecoveredAmount
                  ) {
                    message.error(
                      `Minimum amount is ₹${cumulativeRecoveredAmount.toLocaleString("en-IN")} (cumulative recovered amount)`
                    );
                  }
                }
                return prevItems;
              });
            }, 500);
          }
          return { ...item, [field]: value };
        }
        return item;
      })
    );
  };

  const validateRecoveryConditions = () => {
    if (!recoveryConditions.recoveryType) {
      message.error("Please select Recovery Type");
      return false;
    }
    if (
      !recoveryConditions.recoveryPercentRate ||
      recoveryConditions.recoveryPercentRate <= 0
    ) {
      message.error("Please enter Recovery %");
      return false;
    }
    if (
      !recoveryConditions.recoveryCapPercent ||
      recoveryConditions.recoveryCapPercent <= 0
    ) {
      message.error("Please enter Recovery Cap %");
      return false;
    }
    if (
      recoveryConditions.interestCharged &&
      (!recoveryConditions.interestRate || recoveryConditions.interestRate <= 0)
    ) {
      message.error("Please enter Interest Rate");
      return false;
    }
    return true;
  };

  const handleUpdateRecoveryConditions = async () => {
    if (!validateRecoveryConditions()) {
      return;
    }

    try {
      setUpdatingRecovery(true);

      const requestBody: any = {
        workOrderId: workOrder.workOrderId,
        type: "Mobilization Advance",
        lines: [],
        recoveryType: recoveryConditions.recoveryType,
        recoveryPercentRate: recoveryConditions.recoveryPercentRate,
        recoveryCapPercent: recoveryConditions.recoveryCapPercent,
        interestCharged: recoveryConditions.interestCharged,
        interestRate: recoveryConditions.interestRate,
      };

      const response: any = await workOrderApis.submitRiskFlags(requestBody);

      if (response && response.status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Recovery conditions updated successfully",
        }).then(() => {
          const searchParams = new URLSearchParams(location.search);
          const flowType = searchParams.get("flowType");
          const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
          navigate(`/work-order-view/${id}${flowTypeQuery}`, {
            state: { fromRiskflagsOrAdvance: true },
          });
        });
      } else {
        throw new Error(
          response?.message || "Failed to update recovery conditions"
        );
      }
    } catch (error: any) {
      console.error("Error updating recovery conditions:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update recovery conditions. Please try again.";
      message.error(errorMessage);
    } finally {
      setUpdatingRecovery(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      // Note: API only accepts "Risk Flag" or "Mobilization Advance"
      const type = "Mobilization Advance";

      // Only send new items (existing items are read-only)
      const lines = advanceItems
        .filter((item) => !item.isExisting)
        .map((item) => {
          const line: any = {
            name: item.name || "",
            isChargable: true, // Always true
            totalAmount: item.amount || 0,
            notes: item.remarks || "",
          };
          // Include workOrderAdvanceDeductionId only for existing items
          if (item.workOrderAdvanceDeductionId) {
            line.workOrderAdvanceDeductionId = item.workOrderAdvanceDeductionId;
          }
          return line;
        });

      const requestBody: any = {
        workOrderId: workOrder.workOrderId,
        type: type,
        lines: lines,
      };

      // Include recovery conditions only when mobilizationRecovery is not set
      if (showRecoveryConditions) {
        requestBody.recoveryType = recoveryConditions.recoveryType;
        requestBody.recoveryPercentRate = recoveryConditions.recoveryPercentRate;
        requestBody.recoveryCapPercent = recoveryConditions.recoveryCapPercent;
        requestBody.interestCharged = recoveryConditions.interestCharged;
        requestBody.interestRate = recoveryConditions.interestRate;
      }

      const response: any = await workOrderApis.submitRiskFlags(requestBody);

      if (response && response.status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Advance submitted successfully",
        }).then(() => {
          const searchParams = new URLSearchParams(location.search);
          const flowType = searchParams.get("flowType");
          const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
          navigate(`/work-order-view/${id}${flowTypeQuery}`, {
            state: { fromRiskflagsOrAdvance: true },
          });
        });
      } else {
        throw new Error(response?.message || "Failed to submit advance");
      }
    } catch (error: any) {
      console.error("Error submitting advance:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit advance. Please try again.";
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle>Loading...</BlockTitle>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <Block>
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        </Block>
      </Content>
    );
  }

  if (!workOrder) {
    return (
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle>Work Order Not Found</BlockTitle>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
      </Content>
    );
  }

  return (
    <>
      <Head title="Add Advance" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>
                <PlusCircleOutlined className="me-2" />
                Add Advance - {workOrder.workOrderNumber}
              </BlockTitle>
              <div className="nk-block-des text-soft">
                <p>Add advance details for work order</p>
              </div>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="light"
                outline
                className="bg-white d-none d-sm-inline-flex"
                onClick={() => {
                  const searchParams = new URLSearchParams(location.search);
                  const flowType = searchParams.get("flowType");
                  const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
                  navigate(`/work-order-view/${id}${flowTypeQuery}`, {
                    state: { fromRiskflagsOrAdvance: true },
                  });
                }}
              >
                <Icon name="arrow-left"></Icon>
                <span>Back</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Card className="card-bordered">
            <div className="card-inner">
              <Form form={form} layout="vertical">
                {/* Header Information */}
                <Row
                  className="g-3"
                  style={{
                    marginBottom: 24,
                    padding: 16,
                    background: "#f5f5f5",
                    borderRadius: 8,
                  }}
                >
                  <Col md="3">
                    <div>
                      <strong>WO #:</strong> {workOrder.workOrderNumber}
                    </div>
                  </Col>
                  <Col md="3">
                    <div>
                      <strong>Contractor:</strong> {workOrder.contractorName}
                    </div>
                  </Col>
                  <Col md="3">
                    <div>
                      <strong>Site:</strong> {workOrder.siteName}
                    </div>
                  </Col>
                  <Col md="3">
                    <div>
                      <strong>Total Value:</strong> ₹
                      {workOrder.grossValue?.toLocaleString("en-IN") || "0"}
                    </div>
                  </Col>
                </Row>

                {/* Recovery Conditions - shown when mobilization advance is absent,
                    or when present but recovery % / recovery cap % is not captured */}
                {showRecoveryConditions && (
                <AntCard
                  size="small"
                  title="Recovery Conditions"
                  style={{ marginBottom: 24 }}
                >
                  {mobilizationAdvanceAmount > 0 && (
                    <AntAlert
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message={
                        <span>
                          Mobilization advance amount of{" "}
                          <strong>
                            ₹{mobilizationAdvanceAmount.toLocaleString("en-IN")}
                          </strong>{" "}
                          is already present. Please capture the recovery
                          conditions below.
                        </span>
                      }
                    />
                  )}
                  <Row className="g-3">
                    <Col md="3">
                      <div className="form-group">
                        <label className="form-label">Recovery Type</label>
                        <Select
                          value={recoveryConditions.recoveryType}
                          onChange={(value) =>
                            setRecoveryConditions((prev) => ({
                              ...prev,
                              recoveryType: value,
                            }))
                          }
                          style={{ width: "100%" }}
                          options={[
                            {
                              label: "Each RA Bill",
                              value: "Each RA Bill",
                            },
                            {
                              label: "Final Bill",
                              value: "Final Bill",
                            },
                          ]}
                          placeholder="Select recovery type"
                        />
                      </div>
                    </Col>
                    <Col md="2">
                      <div className="form-group">
                        <label className="form-label">Recovery %</label>
                        <InputNumber
                          min={0}
                          max={100}
                          value={recoveryConditions.recoveryPercentRate}
                          onChange={(value) =>
                            setRecoveryConditions((prev) => ({
                              ...prev,
                              recoveryPercentRate: value || 0,
                            }))
                          }
                          style={{ width: "100%" }}
                          placeholder="Recovery %"
                        />
                      </div>
                    </Col>
                    <Col md="2">
                      <div className="form-group">
                        <label className="form-label">
                          Recovery Cap %
                        </label>
                        <InputNumber
                          min={0}
                          max={100}
                          value={recoveryConditions.recoveryCapPercent}
                          onChange={(value) =>
                            setRecoveryConditions((prev) => ({
                              ...prev,
                              recoveryCapPercent: value || 0,
                            }))
                          }
                          style={{ width: "100%" }}
                          placeholder="Cap % of WO"
                        />
                        <small className="text-muted">
                          Upto % of work order value
                        </small>
                      </div>
                    </Col>
                    <Col md="2">
                      <div className="form-group">
                        <label className="form-label">Interest Charged</label>
                        <div>
                          <Switch
                            checked={recoveryConditions.interestCharged}
                            onChange={(checked) =>
                              setRecoveryConditions((prev) => ({
                                ...prev,
                                interestCharged: checked,
                                interestRate: checked ? prev.interestRate : 0,
                              }))
                            }
                          />
                        </div>
                      </div>
                    </Col>
                    {recoveryConditions.interestCharged && (
                      <Col md="2">
                        <div className="form-group">
                          <label className="form-label">
                            Interest Rate (%)
                          </label>
                          <InputNumber
                            min={0}
                            max={100}
                            value={recoveryConditions.interestRate}
                            onChange={(value) =>
                              setRecoveryConditions((prev) => ({
                                ...prev,
                                interestRate: value || 0,
                              }))
                            }
                            style={{ width: "100%" }}
                            placeholder="Interest rate"
                          />
                        </div>
                      </Col>
                    )}
                  </Row>
                  <div
                    style={{
                      textAlign: "right",
                      marginTop: 16,
                      borderTop: "1px solid #f0f0f0",
                      paddingTop: 12,
                    }}
                  >
                    <AntButton
                      type="primary"
                      onClick={handleUpdateRecoveryConditions}
                      loading={updatingRecovery}
                      disabled={submitting}
                    >
                      Update
                    </AntButton>
                  </div>
                </AntCard>
                )}

                {/* Advance Details Table */}
                <Row className="mb-4">
                  <Col md="12">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 16,
                      }}
                    >
                      <h6 style={{ margin: 0 }}>Advance Items</h6>
                      <AntButton
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddRow}
                      >
                        Add Row
                      </AntButton>
                    </div>
                    <Table
                      dataSource={advanceItems}
                      rowKey="id"
                      pagination={false}
                      bordered
                      size="small"
                      columns={[
                        {
                          title: "S.No",
                          key: "sno",
                          width: 80,
                          align: "center",
                          render: (_: any, __: any, index: number) => index + 1,
                        },
                        {
                          title: (
                            <span>
                              Name <span style={{ color: "red" }}>*</span>
                            </span>
                          ),
                          key: "name",
                          width: 300,
                          render: (_: any, record: any) => (
                            <Input
                              value={record.name}
                              onChange={(e) =>
                                handleItemChange(
                                  record.id,
                                  "name",
                                  e.target.value
                                )
                              }
                              placeholder="Enter name"
                              disabled={record.isExisting}
                            />
                          ),
                        },
                        {
                          title: (
                            <span>
                              Amount <span style={{ color: "red" }}>*</span>
                            </span>
                          ),
                          key: "amount",
                          width: 200,
                          render: (_: any, record: any) => (
                            <InputNumber
                              style={{ width: "100%" }}
                              value={record.amount}
                              onChange={(value) =>
                                handleItemChange(record.id, "amount", value)
                              }
                              min={0}
                              precision={2}
                              controls={false}
                              formatter={indianCurrencyFormatter}
                              parser={indianCurrencyParser}
                              placeholder="Enter amount"
                              disabled={record.isExisting}
                              onKeyPress={(e) => {
                                // Allow: backspace, delete, tab, escape, enter, decimal point, and numbers
                                if (
                                  !/[0-9.]/.test(e.key) &&
                                  ![
                                    "Backspace",
                                    "Delete",
                                    "Tab",
                                    "Escape",
                                    "Enter",
                                  ].includes(e.key)
                                ) {
                                  e.preventDefault();
                                }
                              }}
                            />
                          ),
                        },
                        // {
                        //   title: "Cumulative Recovered Amount",
                        //   key: "recoveredAmount",
                        //   width: 250,
                        //   align: "right",
                        //   render: (_: any, record: any) => {
                        //     if (record.isExisting) {
                        //       const totalRecovered =
                        //         record.totalRecoveredAmount || 0;
                        //       const totalPending =
                        //         record.totalPendingRecoveredAmount || 0;
                        //       const cumulativeAmount =
                        //         totalRecovered + totalPending;
                        //       return (
                        //         <div style={{ textAlign: "right" }}>
                        //           <span style={{ fontWeight: 500 }}>
                        //             ₹
                        //             {cumulativeAmount.toLocaleString("en-IN", {
                        //               maximumFractionDigits: 2,
                        //             })}
                        //           </span>
                        //         </div>
                        //       );
                        //     }
                        //     return <span style={{ color: "#999" }}>-</span>;
                        //   },
                        // },
                        {
                          title: (
                            <span>
                              Remarks <span style={{ color: "red" }}>*</span>
                            </span>
                          ),
                          key: "remarks",
                          render: (_: any, record: any) => (
                            <Input
                              value={record.remarks}
                              onChange={(e) =>
                                handleItemChange(
                                  record.id,
                                  "remarks",
                                  e.target.value
                                )
                              }
                              placeholder="Enter remarks"
                              disabled={record.isExisting}
                            />
                          ),
                        },
                        {
                          title: "Action",
                          key: "action",
                          width: 100,
                          align: "center",
                          render: (_: any, record: any) => (
                            <AntButton
                              type="link"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => handleDeleteRow(record.id)}
                              disabled={record.isExisting}
                            />
                          ),
                        },
                      ]}
                    />
                  </Col>
                </Row>

                {/* Action Buttons */}
                <div
                  style={{
                    textAlign: "right",
                    borderTop: "1px solid #f0f0f0",
                    paddingTop: 16,
                    marginTop: 16,
                  }}
                >
                  <Button
                    color="light"
                    onClick={() => {
                      const searchParams = new URLSearchParams(location.search);
                      const flowType = searchParams.get("flowType");
                      const flowTypeQuery = flowType
                        ? `?flowType=${flowType}`
                        : "";
                      navigate(`/work-order-view/${id}${flowTypeQuery}`, {
                        state: { fromRiskflagsOrAdvance: true },
                      });
                    }}
                    style={{ marginRight: 8 }}
                    disabled={submitting || updatingRecovery}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    onClick={handleSubmit}
                    disabled={submitting || updatingRecovery}
                  >
                    {submitting ? (
                      <>
                        <Spinner size="sm" /> Submitting...
                      </>
                    ) : (
                      <>Submit</>
                    )}
                  </Button>
                </div>
              </Form>
            </div>
          </Card>
        </Block>
      </Content>
    </>
  );
};

export default WorkOrderAdvance;
